/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import type { UseTranslationResponse } from 'react-i18next';
import sharedTranslations from '../../../locales/shared.json';

export const SHARED_NAMESPACE = 'shared';

type GeoWebLanguage = 'en' | 'fi' | 'no' | 'nl';
const geowebLanguages: GeoWebLanguage[] = ['en', 'fi', 'no', 'nl'];
interface TranslationToLoad {
  ns: string;
  translations: Record<GeoWebLanguage, Record<string, string>>;
}

export const loadAllTranslations = (
  i18nInstance: typeof i18n,
  translationsToLoad: TranslationToLoad[],
): void => {
  translationsToLoad.forEach(({ ns, translations }) => {
    geowebLanguages.forEach((lang) => {
      i18nInstance.addResourceBundle(lang, ns, translations[lang]);
    });
  });
};

export const safelyInitI18nForTestsAndStories = (
  i18nInstance: typeof i18n,
  namespace: string,
  translationsToLoad: TranslationToLoad[],
  initLanguage?: string,
): void => {
  // This check ensures that we don't init the library many times.
  // - in tests, the init is done globally, so this should not init
  // - in stories, this function should init, but only once if called many times
  if (!i18nInstance.isInitialized && !i18nInstance.isInitializing) {
    void i18nInstance.use(initReactI18next).init({
      lng: initLanguage ?? 'en',
      ns: namespace,
      interpolation: {
        escapeValue: false,
      },
      resources: {},
    });
  }
  loadAllTranslations(i18nInstance, translationsToLoad);
};

export const initSharedI18n = (): void => {
  safelyInitI18nForTestsAndStories(i18n, SHARED_NAMESPACE, [
    { ns: SHARED_NAMESPACE, translations: sharedTranslations },
  ]);
};

export const translateInTestsAndStories = (
  key: string,
  params: Record<string, string | number> | undefined = undefined,
): string => i18n.t(key, { ns: SHARED_NAMESPACE, ...params });

export const useSharedTranslation = (): UseTranslationResponse<
  typeof SHARED_NAMESPACE,
  typeof i18n
> => useTranslation([SHARED_NAMESPACE]);

export { i18n };
